import {
    Box,
    Container,
    Button,
    Typography,
    Card,
    CircularProgress,
    TextField,
    Alert,
    Link as MuiLink,
    Stack,
    InputAdornment,
    IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../../react-query/auth-queries';
import { useUser } from '../../providers/user-provider';
import { useState } from 'react';
import { ROUTES } from '../../router/routes';
import { brand } from '../../shared-theme/themePrimitives';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { PodcastIcon } from '@/components/icons/PodcastIcon';

export default function LoginPage() {
    const navigate = useNavigate();
    const { setToken, setUser } = useUser();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const { mutateAsync: login, isPending } = useLogin();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            const response = await login({ email, password });
            if (response.success && response.data) {
                const { accessToken, refreshToken, user } = response.data;
                setToken('accessToken', accessToken);
                if (refreshToken) setToken('refreshToken', refreshToken);
                if (user) setUser(user);
                
                // Check verification status explicitly
                // Note: Some backend implementations might return isEmailVerified as part of user object,
                // while others might enforce it via HTTP 401/403.
                // Assuming we trust the 'user' object from the response:
                if (user && user.isEmailVerified === false) {
                    navigate(ROUTES.CHECK_EMAIL.path);
                } else {
                    navigate(ROUTES.DASHBOARD.path);
                }
            } else {
                setError(response.message || 'Login failed');
            }
        } catch (err: any) {
            console.error("Login error:", err);
            const errorMessage = err.response?.data?.message || 'An error occurred during login';
            
            if (errorMessage.toLowerCase().includes('verify') || errorMessage.toLowerCase().includes('active')) {
                 // If login fails specifically due to unverified email, we temporarily set the user context 
                 // with just the email so the CheckEmailPage works.
                 setUser({ email: email } as any); 
                 navigate(ROUTES.CHECK_EMAIL.path);
            } else {
                setError(errorMessage);
            }
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(ellipse at 50% -20%, rgba(210, 225, 255, 0.5) 0%, rgba(255,255,255,0) 70%)',
                bgcolor: '#FAFAFA',
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="sm" >
                <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                        <PodcastIcon size={48} color={brand[500]} />
                    </Box>
                    <Typography variant="h3" component="h1" gutterBottom fontWeight={800} sx={{ color: '#0F172A', fontSize: { xs: '1.75rem', md: '2rem' } }}>
                        Welcome Back
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                        Sign in to continue to your account
                    </Typography>
                </Box>

                <Card 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 3, sm: 5 }, 
                        borderRadius: '24px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                    }}
                >
                    {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}
                    
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2.5}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                    Email Address
                                </Typography>
                                <TextField
                                    required
                                    fullWidth
                                    id="email"
                                    name="email"
                                    placeholder="john@company.com"
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                />
                            </Box>

                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#334155' }}>
                                        Password
                                    </Typography>
                                    <MuiLink 
                                        component="button" 
                                        variant="caption" 
                                        onClick={() => navigate(ROUTES.FORGOT_PASSWORD.path)}
                                        type="button"
                                        sx={{ 
                                            textDecoration: 'none', 
                                            fontWeight: 600, 
                                            color: brand[500] 
                                        }}
                                    >
                                        Forgot password?
                                    </MuiLink>
                                </Box>
                                <TextField
                                    required
                                    fullWidth
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={isPending}
                                sx={{ 
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    bgcolor: brand[400],
                                    '&:hover': { bgcolor: brand[500] },
                                    boxShadow: '0 10px 20px -10px rgba(225, 89, 27, 0.4)'
                                }}
                            >
                                {isPending ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                            </Button>
                        </Stack>
                    </Box>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Don't have an account?{' '}
                            <MuiLink 
                                component="button"
                                onClick={() => navigate(ROUTES.SIGNUP.path)}
                                sx={{ 
                                    fontWeight: 600, 
                                    textDecoration: 'none',
                                    color: brand[500] 
                                }}
                            >
                                Sign up
                            </MuiLink>
                        </Typography>
                    </Box>
                </Card>
            </Container>
        </Box>
    );
}
